import * as React from "react";
import {
  LoadingSpinner,
  PrimaryBlueButton,
  SecondaryBlueButton,
  Severity,
  Toggle
} from "@velaro/velaro-component-lib";
import useApi from "@/hooks/useApi";
import useToastAlert from "@/hooks/useToastAlert";
import FormGroup from "@/components/FormGroup";

interface TenantStatus {
  site_id: number;
  site_info: any;
  tenant: any;
  subscription: any;
  v10_subscription: any;
  actions_taken: string[];
  status: string;
}

export default function DatabaseTool() {
  const api = useApi();
  const { displayToast } = useToastAlert();

  const [siteId, setSiteId] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [status, setStatus] = React.useState<TenantStatus | null>(null);

  async function checkAndFixSite() {
    if (!siteId) {
      displayToast(Severity.Error, "Please enter a Site ID");
      return;
    }

    setLoading(true);
    try {
      const response = await api.admin.post("DatabaseTool/AutoVerify", {
        siteId: parseInt(siteId)
      });

      const data = await response.json();
      setStatus(data);

      if (data.actions_taken && data.actions_taken.length > 0) {
        const hasChanges = data.actions_taken.some((action: string) =>
          action.includes("Created") || action.includes("✅")
        );

        if (hasChanges) {
          displayToast(Severity.Success, "Site configured successfully!");
        } else {
          displayToast(Severity.Info, "Site already configured correctly");
        }
      }
    } catch (error: any) {
      displayToast(Severity.Error, `Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }

  async function toggleFeature(feature: string, enabled: boolean) {
    if (!siteId) return;

    try {
      await api.admin.post("DatabaseTool/ToggleFeature", {
        siteId: parseInt(siteId),
        feature,
        enabled
      });

      displayToast(Severity.Success, `${feature} ${enabled ? "enabled" : "disabled"}`);

      // Refresh status
      checkAndFixSite();
    } catch (error: any) {
      displayToast(Severity.Error, `Error: ${error.message}`);
    }
  }

  const features = [
    { key: 'EnableWeb', label: 'Web Messenger' },
    { key: 'EnableSms', label: 'SMS Channel' },
    { key: 'EnableEmail', label: 'Email Channel' },
    { key: 'EnableWhatsapp', label: 'WhatsApp' },
    { key: 'EnableFacebook', label: 'Facebook Messenger' },
    { key: 'EnableNetsuite', label: 'NetSuite Integration' },
    { key: 'EnableAutomation', label: 'Automation' },
    { key: 'EnableAI', label: 'AI Chatbots' },
    { key: 'EnableWorkflowRules', label: 'Workflow Rules' }
  ];

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-800 mb-2">
          Database Administration Tool
        </h1>
        <p className="text-slate-600">
          Verify and configure messaging tenant and subscription settings for any site
        </p>
      </div>

      {/* Site ID Input */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <FormGroup label="Site ID">
          <div className="flex gap-4">
            <input
              type="number"
              value={siteId}
              onChange={(e) => setSiteId(e.target.value)}
              placeholder="Enter site ID to check"
              className="flex-1 px-4 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <PrimaryBlueButton
              label={loading ? "Checking..." : "Check & Fix"}
              onClick={checkAndFixSite}
              disabled={loading || !siteId}
            />
          </div>
        </FormGroup>

        {loading && (
          <div className="mt-4 flex justify-center">
            <LoadingSpinner />
          </div>
        )}
      </div>

      {/* Status Display */}
      {status && !loading && (
        <>
          {/* Actions Taken */}
          {status.actions_taken && status.actions_taken.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-blue-900 mb-2">Actions Taken:</h3>
              <ul className="list-disc list-inside space-y-1">
                {status.actions_taken.map((action, i) => (
                  <li key={i} className="text-blue-800">{action}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Status Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {/* Site Info */}
            <div className={`rounded-lg p-4 border-2 ${
              status.site_info ? 'bg-green-50 border-green-500' : 'bg-red-50 border-red-500'
            }`}>
              <h3 className="font-semibold mb-2">Site Information</h3>
              <div className={`inline-block px-3 py-1 rounded-full text-sm font-semibold mb-2 ${
                status.site_info ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
              }`}>
                {status.site_info ? 'Found' : 'Not Found'}
              </div>
              {status.site_info && (
                <div className="text-sm space-y-1">
                  <div><strong>Company:</strong> {status.site_info.CompanyName}</div>
                  <div><strong>Created:</strong> {new Date(status.site_info.DateCreated).toLocaleDateString()}</div>
                </div>
              )}
            </div>

            {/* Tenant */}
            <div className={`rounded-lg p-4 border-2 ${
              status.tenant ? 'bg-green-50 border-green-500' : 'bg-red-50 border-red-500'
            }`}>
              <h3 className="font-semibold mb-2">Messaging Tenant</h3>
              <div className={`inline-block px-3 py-1 rounded-full text-sm font-semibold mb-2 ${
                status.tenant ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
              }`}>
                {status.tenant ? 'Exists' : 'Missing'}
              </div>
              {status.tenant && (
                <div className="text-sm">
                  <div><strong>Tenant ID:</strong> {status.tenant.Id}</div>
                </div>
              )}
            </div>

            {/* Subscription */}
            <div className={`rounded-lg p-4 border-2 ${
              status.subscription ? 'bg-green-50 border-green-500' : 'bg-red-50 border-red-500'
            }`}>
              <h3 className="font-semibold mb-2">Subscription</h3>
              <div className={`inline-block px-3 py-1 rounded-full text-sm font-semibold mb-2 ${
                status.subscription ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
              }`}>
                {status.subscription ? 'Exists' : 'Missing'}
              </div>
              {status.subscription && (
                <div className="text-sm">
                  <div><strong>Sub ID:</strong> {status.subscription.Id}</div>
                </div>
              )}
            </div>

            {/* V10 License */}
            <div className={`rounded-lg p-4 border-2 ${
              status.v10_subscription?.Status === 'Active' ? 'bg-green-50 border-green-500' : 'bg-yellow-50 border-yellow-500'
            }`}>
              <h3 className="font-semibold mb-2">V10 License</h3>
              <div className={`inline-block px-3 py-1 rounded-full text-sm font-semibold mb-2 ${
                status.v10_subscription?.Status === 'Active' ? 'bg-green-500 text-white' : 'bg-yellow-500 text-white'
              }`}>
                {status.v10_subscription?.Status || 'Not Found'}
              </div>
              {status.v10_subscription && (
                <div className="text-sm">
                  <div><strong>Expires:</strong> {new Date(status.v10_subscription.ExpirationDate).toLocaleDateString()}</div>
                </div>
              )}
            </div>
          </div>

          {/* Feature Toggles */}
          {status.subscription && (
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-xl font-bold text-slate-800 mb-4">Feature Flags</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {features.map((feature) => (
                  <div key={feature.key} className="flex items-center justify-between p-3 border border-slate-200 rounded-md">
                    <span className="text-slate-700">{feature.label}</span>
                    <Toggle
                      toggled={status.subscription[feature.key] === 1}
                      changeToggle={() => toggleFeature(feature.key, status.subscription[feature.key] !== 1)}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Instructions */}
      {!status && !loading && (
        <div className="bg-slate-50 rounded-lg p-6 text-slate-600">
          <h3 className="font-semibold text-slate-800 mb-2">How to use:</h3>
          <ol className="list-decimal list-inside space-y-2">
            <li>Enter a Site ID above</li>
            <li>Click "Check & Fix" to verify the site configuration</li>
            <li>If tenant or subscription is missing, they will be created automatically</li>
            <li>Toggle feature flags as needed</li>
            <li>All changes are safe and only affect the specified site</li>
          </ol>
        </div>
      )}
    </div>
  );
}
